import React, { useState, useEffect, useMemo } from 'react';
import loadingSpinner from '../images/loading-spinner.svg';

const EventLogs = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [userFilter, setUserFilter] = useState('all');
    const [eventTypeFilter, setEventTypeFilter] = useState('all');
    const [dateRangeFilter, setDateRangeFilter] = useState({
        startDate: '',
        endDate: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [expandedRows, setExpandedRows] = useState(new Set());

    //const EVENTS_ENDPOINT = 'https://eomszowok45vf0t.m.pipedream.net';  // OLD

    const EVENTS_ENDPOINT = 'https://eo9dhp2c1nzogqx.m.pipedream.net/api/UserSetup/FetchEvents';   //NEW

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await fetch(EVENTS_ENDPOINT, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setEvents(data.events || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching events:', error);
            setError(`Failed to load events: ${error.message}`);
            setLoading(false);
        }
    };

    const uniqueUsers = useMemo(() => {
        const users = events.map(event => ({
            userId: event.userId,
            email: event.userEmail
        }));
        const uniqueMap = new Map();
        users.forEach(user => {
            if (user.userId) {
                uniqueMap.set(user.userId, user);
            }
        });
        return Array.from(uniqueMap.values());
    }, [events]);

    const eventTypes = useMemo(() => {
        const types = [...new Set(events.map(event => event.eventName))];
        return types.sort();
    }, [events]);

    const filteredEvents = useMemo(() => {
        let filtered = [...events];

        if (searchTerm) {
            filtered = filtered.filter(event => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    event.eventName?.toLowerCase().includes(searchLower) ||
                    event.userEmail?.toLowerCase().includes(searchLower) ||
                    event.pathname?.toLowerCase().includes(searchLower) ||
                    JSON.stringify(event.eventData).toLowerCase().includes(searchLower)
                );
            });
        }

        if (userFilter !== 'all') {
            filtered = filtered.filter(event => event.userId === userFilter);
        }

        if (eventTypeFilter !== 'all') {
            filtered = filtered.filter(event => event.eventName === eventTypeFilter);
        }

        if (dateRangeFilter.startDate || dateRangeFilter.endDate) {
            filtered = filtered.filter(event => {
                if (!event.timestamp) return false;
                const eventDate = new Date(event.timestamp);

                const start = dateRangeFilter.startDate
                    ? new Date(dateRangeFilter.startDate + 'T00:00:00.000Z')
                    : new Date('1900-01-01');

                const end = dateRangeFilter.endDate
                    ? new Date(dateRangeFilter.endDate + 'T23:59:59.999Z')
                    : new Date('2100-12-31');

                return eventDate >= start && eventDate <= end;
            });
        }

        // Sort by timestamp descending (newest first)
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return filtered;
    }, [events, searchTerm, userFilter, eventTypeFilter, dateRangeFilter]);

    const paginatedEvents = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return filteredEvents.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredEvents, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredEvents.length / rowsPerPage);

    const toggleRowExpansion = (eventId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(eventId)) {
            newExpanded.delete(eventId);
        } else {
            newExpanded.add(eventId);
        }
        setExpandedRows(newExpanded);
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getEventIcon = (eventName) => {
        const icons = {
            'page_view': '👁️',
            'button_click': '🖱️',
            'form_submission': '📝',
            'search': '🔍',
            'filter_applied': '🔧',
            'error': '❌',
            'modal_open': '🪟',
            'modal_close': '❎',
            'data_export': '📊',
            'user_action': '👤',
            'login': '🔑',
            'logout': '🚪',
            'session_start': '▶️',
            'page_hidden': '👻',
            'page_visible': '👀',
            'page_unload': '🛑',
            'authorization_check': '🔒',
            'access_denied': '🚫'
        };
        return icons[eventName] || '📌';
    };

    if (loading) {
        return (
            <div className="event-logs-loading">
                <img src={loadingSpinner} alt="Loading..." className="loading-svg" />
                <p>Loading event logs...</p>
            </div>
        );
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="event-logs-container">
            <div className="event-logs-header">

            </div>

            <div className="event-logs-filters">
                <div className="filter-row">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search events..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="search-input"
                        />
                    </div>

                    <div className="filter-group">
                        <label>User:</label>
                        <select
                            value={userFilter}
                            onChange={(e) => {
                                setUserFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="filter-select"
                        >
                            <option value="all">All Users</option>
                            {uniqueUsers.map(user => (
                                <option key={user.userId} value={user.userId}>
                                    {user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Event Type:</label>
                        <select
                            value={eventTypeFilter}
                            onChange={(e) => {
                                setEventTypeFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="filter-select"
                        >
                            <option value="all">All Events</option>
                            {eventTypes.map(type => (
                                <option key={type} value={type}>
                                    {type.replace(/_/g, ' ').toUpperCase()}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>From:</label>
                        <input
                            type="date"
                            value={dateRangeFilter.startDate}
                            onChange={(e) => {
                                setDateRangeFilter(prev => ({ ...prev, startDate: e.target.value }));
                                setCurrentPage(1);
                            }}
                            className="date-input"
                        />
                    </div>

                    <div className="filter-group">
                        <label>To:</label>
                        <input
                            type="date"
                            value={dateRangeFilter.endDate}
                            onChange={(e) => {
                                setDateRangeFilter(prev => ({ ...prev, endDate: e.target.value }));
                                setCurrentPage(1);
                            }}
                            className="date-input"
                        />
                    </div>

                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setUserFilter('all');
                            setEventTypeFilter('all');
                            setDateRangeFilter({ startDate: '', endDate: '' });
                            setCurrentPage(1);
                        }}
                        className="btn-reset-filters"
                    >
                        Reset
                    </button>
                </div>
            </div>



            <div className="event-logs-table">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th style={{ width: '180px' }}>Timestamp</th>
                            <th style={{ width: '200px' }}>User</th>
                            <th style={{ width: '150px' }}>Event</th>
                            <th style={{ width: '200px' }}>Page</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedEvents.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                    No events found matching the filters
                                </td>
                            </tr>
                        ) : (
                            paginatedEvents.map((event) => (
                                <React.Fragment key={event.eventId}>
                                    <tr
                                        className="event-row"
                                        onClick={() => toggleRowExpansion(event.eventId)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="event-icon">
                                            {getEventIcon(event.eventName)}
                                        </td>
                                        <td className="event-timestamp">
                                            {formatTimestamp(event.timestamp)}
                                        </td>
                                        <td className="event-user">
                                            <div className="user-info">
                                                <div className="user-email">{event.userEmail}</div>
                                                <div className="user-role">{event.userRole}</div>
                                            </div>
                                        </td>
                                        <td className="event-name">
                                            <span className={`event-badge ${event.eventName}`}>
                                                {event.eventName.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td className="event-page">
                                            {event.pathname}
                                        </td>
                                        <td className="event-summary">
                                            {event.eventData && Object.keys(event.eventData).length > 0 ? (
                                                <span className="has-details">
                                                    {expandedRows.has(event.eventId) ? '▼' : '▶'} View details
                                                </span>
                                            ) : (
                                                <span className="no-details">-</span>
                                            )}
                                        </td>
                                    </tr>
                                    {expandedRows.has(event.eventId) && event.eventData && (
                                        <tr className="event-details-row">
                                            <td colSpan="6">
                                                <div className="event-details">
                                                    <h4>Event Details:</h4>
                                                    <pre>{JSON.stringify(event.eventData, null, 2)}</pre>

                                                    <h4>Session Info:</h4>
                                                    <div className="session-info">
                                                        <p><strong>Session ID:</strong> {event.sessionId}</p>
                                                        <p><strong>URL:</strong> {event.url}</p>
                                                        <p><strong>Referrer:</strong> {event.referrer || 'Direct'}</p>
                                                        <p><strong>Screen:</strong> {event.screenResolution}</p>
                                                        <p><strong>Viewport:</strong> {event.viewport}</p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                    >
                        Previous
                    </button>

                    <div className="page-numbers">
                        {[...Array(totalPages)].map((_, index) => {
                            const pageNum = index + 1;
                            if (
                                pageNum === 1 ||
                                pageNum === totalPages ||
                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                            ) {
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            } else if (
                                pageNum === currentPage - 2 ||
                                pageNum === currentPage + 2
                            ) {
                                return <span key={pageNum} className="page-dots">...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default EventLogs;